import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import {
  HelpCircle,
  MessageSquare,
  Users,
  Share2,
  User,
  Sparkles,
  Settings,
  Mail,
} from "lucide-react";
import { DiscordIcon } from "@/components/icons/DiscordIcon";
import { SEO } from "@/components/seo/SEO";
import { GlassmorphicCard, GradientText, FloatingShapes } from "@/components/marketing/GlassmorphicCard";

const FAQ = () => {
  const navigate = useNavigate();

  const faqSections = [
    {
      title: "Getting Started",
      icon: HelpCircle,
      gradient: "from-blue-500 to-sky-600",
      questions: [
        {
          q: "What is Lyder Talk?",
          a: "Lyder Talk is an AI-powered speaking practice application designed to help language learners improve their conversation skills. It generates custom topics, practice questions, and vocabulary exercises to help you practice speaking in a structured way.",
        },
        {
          q: "How do I get started?",
          a: "Simply sign up for a free account, and you can start creating speaking practice topics immediately. You can generate topics using AI, browse public sessions from other learners, or create your own custom topics.",
        },
        {
          q: "Do I need to pay to use Lyder Talk?",
          a: "No! Lyder Talk is free to start. You can create topics, practice questions, and access all core features without any cost. Premium features are in development and will be announced when they’re ready.",
        },
        {
          q: "What languages does Lyder Talk support?",
          a: "Currently, Lyder Talk focuses on English speaking practice. We're working on expanding to support other languages like Spanish, French, and German in the future.",
        },
      ],
    },
    {
      title: "Features & Functionality",
      icon: Sparkles,
      gradient: "from-amber-500 to-orange-600",
      questions: [
        {
          q: "How does AI topic generation work?",
          a: "Our AI analyzes your topic request and generates a comprehensive speaking practice session including relevant questions, vocabulary words with IPA pronunciation, and example sentences. All content includes a tailored speaking guide to help you get started.",
        },
        {
          q: "What are the different topic categories?",
          a: "Lyder Talk organizes topics into 16 categories: Business (work and career), Travel (culture and tourism), Technology (tech and innovation), Social (relationships and community), Lifestyle (health and wellness), Education (learning and study), Entertainment (hobbies and media), Daily Life (routines and habits), Environment (sustainability and nature), Food & Cooking (cuisine and recipes), Finance (money and investing), Health & Fitness (wellness and exercise), Politics (government and policy), Art & Design (creativity and aesthetics), History (past events and heritage), and Sports (athletics and competition). Topics are automatically categorized when created.",
        },
        {
          q: "Can I filter and search for topics?",
          a: "Yes! You can filter topics by category, search by keywords, and sort by various criteria. The filtering system works across all pages - Home, My Topics, and History.",
        },
        {
          q: "What are vocabulary cards?",
          a: "Each topic includes relevant vocabulary words with IPA pronunciation guides, definitions, example sentences, and word types. You can click to reveal definitions and use the built-in text-to-speech feature to hear pronunciations. Additionally, our Vocabulary Mind Map feature provides an interactive visual representation of word relationships, helping you understand connections between concepts and build stronger vocabulary networks.",
        },
        {
          q: "How do I pin a session?",
          a: "You can pin important practice sessions for quick access. Pinned sessions appear at the top of your session list and in your sidebar for easy navigation.",
        },
        {
          q: "Can I delete my practice sessions?",
          a: "Yes, you can delete any of your own practice sessions from the History page or My Topics page. Deleted sessions cannot be recovered, so please be careful.",
        },
      ],
    },
    {
      title: "Study Groups",
      icon: Users,
      gradient: "from-blue-500 to-cyan-600",
      questions: [
        {
          q: "What are Study Groups?",
          a: "Study Groups allow you to collaborate with other language learners. You can create or join groups, share practice sessions within groups, and track group activities together.",
        },
        {
          q: "How do I create a Study Group?",
          a: "Go to the Study Groups page and click 'Create Group'. You can choose to make it public (anyone can join) or private (invitation only). Give your group a name and description.",
        },
        {
          q: "How do I invite others to my Study Group?",
          a: "When you create a group, you'll receive an invitation link. Share this link with others, and they can join your group directly. Group owners and admins can also invite members from the group page.",
        },
        {
          q: "What's the difference between public and private groups?",
          a: "Public groups can be discovered and joined by anyone. Private groups require an invitation link to join, giving you more control over who can access your group.",
        },
        {
          q: "Can I share sessions within a Study Group?",
          a: "Yes! You can share your practice sessions within study groups. Group members can view and practice with shared sessions, making collaborative learning easy.",
        },
        {
          q: "What are group roles?",
          a: "Groups have three roles: Owner (created the group, full control), Admin (can manage members and settings), and Member (can view and share sessions). Owners can promote members to admins.",
        },
      ],
    },
    {
      title: "Sharing & Community",
      icon: Share2,
      gradient: "from-pink-500 to-rose-600",
      questions: [
        {
          q: "How do I share my practice sessions?",
          a: "You can share sessions publicly or within study groups. When viewing a session, use the share buttons to share on social media (Twitter, LinkedIn, Facebook) or copy the session link to share directly.",
        },
        {
          q: "Can others see my shared sessions?",
          a: "If you share a session publicly, it will appear on the Home page's Public Topics section where anyone can view it. Sessions shared within study groups are only visible to group members.",
        },
        {
          q: "Can I make my sessions private?",
          a: "By default, your sessions are private and only visible to you. You choose when to share them publicly or within study groups.",
        },
        {
          q: "How do I explore sessions shared by others?",
          a: "Visit the Home page and switch to the Public Topics tab to browse all publicly shared sessions. You can filter by category, search for specific topics, and view sessions created by other learners.",
        },
      ],
    },
    {
      title: "Account & Authentication",
      icon: User,
      gradient: "from-primary to-blue-600",
      questions: [
        {
          q: "I forgot my password. How do I reset it?",
          a: "On the login page, click 'Forgot Password' and enter your email address. You'll receive instructions to reset your password.",
        },
        {
          q: "Is my data secure?",
          a: "Yes! We use industry-standard security practices including Row Level Security (RLS) policies, encrypted connections, and secure authentication. Your data is private and protected.",
        },
        {
          q: "Can I delete my account?",
          a: "Account deletion is currently handled through our support. Please contact us if you'd like to delete your account and all associated data.",
        },
      ],
    },
    {
      title: "Premium Features",
      icon: Sparkles,
      gradient: "from-blue-500 to-indigo-600",
      questions: [
        {
          q: "What premium features are coming?",
          a: "We're planning features like unlimited daily topics, advanced AI features (voice feedback, conversation simulation), priority support, detailed analytics, and more. We'll announce premium as soon as it launches.",
        },
        {
          q: "Will there be a free tier after premium launches?",
          a: "Yes! We plan to maintain a generous free tier so everyone can access core features. Premium will unlock advanced capabilities and unlimited usage.",
        },
      ],
    },
    {
      title: "Technical Support",
      icon: Settings,
      gradient: "from-slate-500 to-gray-600",
      questions: [
        {
          q: "The app is not loading properly. What should I do?",
          a: "Try refreshing the page, clearing your browser cache, or using a different browser. If the problem persists, please contact our support team.",
        },
        {
          q: "I'm experiencing slow performance. Why?",
          a: "Slow performance can be caused by network issues or high server load. Try again in a few minutes. We're continuously optimizing performance to ensure fast load times.",
        },
        {
          q: "Does Lyder Talk work on mobile devices?",
          a: "Yes! Lyder Talk is fully responsive and works on mobile browsers. We're also planning native mobile apps for iOS and Android in the future.",
        },
        {
          q: "What browsers are supported?",
          a: "Lyder Talk works best on modern browsers including Chrome, Firefox, Safari, and Edge. Make sure you're using an updated version for the best experience.",
        },
        {
          q: "How do I report a bug or issue?",
          a: "If you encounter a bug or have suggestions, please contact us through our support channels. We appreciate your feedback and work to fix issues quickly.",
        },
      ],
    },
  ];

  // Structured data for FAQ page
  const faqStructuredData = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqSections.flatMap(section =>
      section.questions.map(q => ({
        '@type': 'Question',
        name: q.q,
        acceptedAnswer: {
          '@type': 'Answer',
          text: q.a,
        },
      }))
    ),
  };

  return (
    <>
      <SEO
        title="Frequently Asked Questions - Lyder Talk"
        description="Find answers to common questions about Lyder Talk, including getting started, features, study groups, sharing, account management, and technical support."
        url="/faq"
        keywords="Lyder Talk FAQ, help, support, language learning questions, speaking practice help, how to use Lyder Talk"
        structuredData={faqStructuredData}
      />
      <div className="bg-background overflow-hidden">
        {/* Dynamic Background */}
        <div className="fixed inset-0 pointer-events-none">
          <div className="absolute inset-0 bg-gradient-to-br from-background via-background to-muted/30" />
          <FloatingShapes />
          <div className="absolute inset-0 grid-pattern opacity-20" />
          <div className="absolute inset-0 noise-overlay" />
        </div>

        <div className="relative z-10 container mx-auto px-4 py-12 max-w-4xl">

          {/* Header Card */}
          <GlassmorphicCard 
            className="mb-8 text-center"
            padding="lg"
            animatedBorder
          >
            <div className="flex justify-center mb-4">
              <div className="p-4 rounded-full bg-gradient-to-br from-primary/20 to-secondary/20 ring-2 ring-primary/20 animate-pulse-glow">
                <HelpCircle className="w-12 h-12 text-primary" />
              </div>
            </div>
            <h1 className="text-4xl font-bold mb-2">
              <GradientText>Frequently Asked Questions</GradientText>
            </h1>
            <p className="text-lg text-muted-foreground">
              Find answers to common questions about Lyder Talk
            </p>
          </GlassmorphicCard>

          {/* FAQ Sections */}
          <div className="space-y-6">
            {faqSections.map((section, sectionIndex) => {
              const Icon = section.icon;
              return (
                <GlassmorphicCard 
                  key={sectionIndex} 
                  padding="none"
                  className="animate-fade-in-up overflow-hidden"
                  style={{ animationDelay: `${sectionIndex * 100}ms` }}
                >
                  {/* Section Header */}
                  <div className="p-6 pb-0">
                    <div className="flex items-center gap-3 mb-4">
                      <div className={`p-2 rounded-xl bg-gradient-to-br ${section.gradient} shadow-lg`}>
                        <Icon className="w-5 h-5 text-white" />
                      </div>
                      <h2 className="text-xl font-semibold">{section.title}</h2>
                    </div>
                  </div>
                  
                  {/* Accordion */}
                  <div className="px-6 pb-6">
                    <Accordion type="single" collapsible className="w-full">
                      {section.questions.map((faq, index) => (
                        <AccordionItem 
                          key={index} 
                          value={`item-${sectionIndex}-${index}`}
                          className="border-border/30"
                        >
                          <AccordionTrigger className="text-left font-medium hover:text-primary transition-colors duration-300 py-4">
                            {faq.q}
                          </AccordionTrigger>
                          <AccordionContent className="text-muted-foreground leading-relaxed pb-4">
                            {faq.a}
                          </AccordionContent>
                        </AccordionItem>
                      ))}
                    </Accordion>
                  </div>
                </GlassmorphicCard>
              );
            })}
          </div>

          {/* Contact Support */}
          <GlassmorphicCard 
            className="mt-8"
            padding="lg"
            animatedBorder
            hoverGradient="from-primary/10 via-secondary/5 to-accent/10"
          >
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 rounded-xl bg-gradient-to-br from-primary to-blue-600 shadow-lg">
                <MessageSquare className="w-5 h-5 text-white" />
              </div>
              <div>
                <h3 className="text-xl font-semibold">Still have questions?</h3>
                <p className="text-muted-foreground">
                  Can't find what you're looking for? We're here to help!
                </p>
              </div>
            </div>
            <div className="flex flex-col sm:flex-row gap-3 mt-6">
              <Button
                variant="outline"
                className="gap-2 glass glass-hover hover:text-primary"
                onClick={() => navigate("/about")}
              >
                <HelpCircle className="w-4 h-4" />
                Visit About Page
              </Button>
              <Button
                variant="outline"
                className="gap-2 glass glass-hover hover:text-primary"
                onClick={() => {
                  window.location.href = "mailto:support@lydertalk.com";
                }}
              >
                <Mail className="w-4 h-4" />
                Contact Support
              </Button>
              <Button
                variant="outline"
                className="gap-2 glass glass-hover hover:text-primary"
                onClick={() => {
                  window.open("https://discord.gg/ACj3Ncag", '_blank', 'noopener,noreferrer');
                }}
              >
                <DiscordIcon className="w-4 h-4" />
                Give Feedback on Discord
              </Button>
            </div>
          </GlassmorphicCard>
        </div>
      </div>
    </>
  );
};

export default FAQ;
